import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { LayoutUtilsService } from '@app/core/_base/crud';
import { MatDialog } from '@angular/material';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { EquipmentTypeService } from '@app/api/equipmentType.service';
import { EquipmentCategoryService } from '@app/api/equipmentCategory.service';
import { debounceTime, map } from 'rxjs/operators';
import { ModalEquipmentTypeComponent } from '../modal-equipment-type/modal-equipment-type.component';
import { ModalManageBrandsComponent } from '../modal-manage-brands/modal-manage-brands.component';

@Component({
  selector: 'meu-equipment-type-management',
  templateUrl: './equipment-type-management.component.html',
  styleUrls: ['./equipment-type-management.component.scss'],
})
export class EquipmentTypeManagementComponent implements OnInit {
  constructor(
    private layoutUtilsService: LayoutUtilsService,
    public dialog: MatDialog,
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    private equipmentTypeService: EquipmentTypeService,
    private equipmentCategoryService: EquipmentCategoryService
  ) {}

  displayedColumns: string[] = ['Index', 'Category', 'Name', 'NameEn', 'Actions'];
  lstAll: any = [];
  total: any;
  pageSize = 10;
  pageIndex = 1;
  filter: any = '';
  pageSizeOptions: number[] = [5, 10, 20, 100];

  ngOnInit() {
    this.keyUp
      .pipe(
        map((event) => event),
        debounceTime(500)
      )
      .subscribe((value) => {
        this.pageIndex = 1;
        this.pageSize = 10;
        this.filter = 'name|code|category_name|brand_name@=*' + value;
        this.getAll();
      });
    this.getAll();
  }

  public keyUp = new Subject<KeyboardEvent>();

  getAll() {
    this.equipmentTypeService
      .apiEquipmentTypesGet(this.filter, '', this.pageIndex, this.pageSize)
      .subscribe((res) => {
        if (res.success) {
          this.lstAll = res.data.collection;
          this.total = res.data.total;
          this.ref.detectChanges();
        } else {
          this.toastr.error(res.message);
        }
      });
  }

  onPageChange(pageNumber: number) {
    if (typeof pageNumber === 'number') {
      this.pageIndex = pageNumber;
      this.getAll();
      window.scrollTo(0, 0);
    }
  }

  onPageSizeChange(newSize: number) {
    this.pageSize = newSize;
    this.pageIndex = 1;
    this.getAll();
  }

  openModal(id) {
    const dialogRef = this.dialog.open(ModalEquipmentTypeComponent, {
      width: '700px',
      data: { id: id },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result != undefined && result.event != undefined) {
        if (result.event) {
          if (result.type == 'create') {
            this.toastr.success('Tạo mới loại thiết bị thành công');
            this.getAll();
          } else {
            this.toastr.success('Chỉnh sửa loại thiết bị thành công');
            this.getAll();
          }
        } else {
          this.toastr.error(result.message);
        }
      }
    });
  }

  openManageBrands(id: string, name: string) {
    const dialogRef = this.dialog.open(ModalManageBrandsComponent, {
      width: '500px',
      data: { id: id, name: name },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.changed) {
        this.getAll();
      }
    });
  }

  getMoreBrandsTooltip(brands: any[]): string {
    if (!brands || brands.length <= 2) return '';
    return brands.slice(2).map(function (b) { return b.name; }).join(', ');
  }

  deleteItem(element: any) {
    const title = 'Xóa loại thiết bị';
    const description = 'Bạn có chắc chắn muốn xóa loại thiết bị này?';
    const waitDesciption = 'Đang xử lý...';
    const dialogRef = this.layoutUtilsService.deleteElement(title, description, waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const categoryId = element.category_id;
      const categoryName = element.category_name;
      this.equipmentTypeService.apiEquipmentTypesIdDelete(String(element.id)).subscribe((delRes) => {
        if (delRes.success) {
          this.toastr.success('Xoá loại thiết bị thành công');
          if (categoryId && categoryName) {
            this.ensureCategoryAfterTypeDeletion(String(categoryId), categoryName);
          } else {
            this.getAll();
          }
        } else {
          this.toastr.error(delRes.message);
        }
      });
    });
  }

  ensureCategoryAfterTypeDeletion(categoryId: string, categoryName: string) {
    const filters = 'category_id==' + categoryId;
    this.equipmentTypeService.apiEquipmentTypesGet(filters, '', 1, this.pageSize).subscribe((res) => {
      if (res.success && res.data.collection.length > 0) {
        const remainingTypes = res.data.collection;
        this.equipmentCategoryService.apiEquipmentCategoriesIdGet(categoryId).subscribe(
          (catRes) => {
            if (catRes.success) {
              this.getAll();
            } else {
              this.recreateCategoryAndReassign(categoryName, remainingTypes);
            }
          },
          () => {
            this.recreateCategoryAndReassign(categoryName, remainingTypes);
          }
        );
      } else {
        this.getAll();
      }
    });
  }

  recreateCategoryAndReassign(categoryName: string, remainingTypes: any[]) {
    const code = categoryName
      .trim()
      .toUpperCase()
      .replace(/\s+/g, '_')
      .replace(/[^A-Z0-9_]/g, '');
    this.equipmentCategoryService
      .apiEquipmentCategoriesPost({ name: categoryName.trim(), code: code, name_en: '', description: '' })
      .subscribe((createRes) => {
        if (createRes.success && createRes.data && createRes.data.id) {
          const newCategoryId = String(createRes.data.id);
          const updatePromises = remainingTypes.map((type) =>
            this.equipmentTypeService
              .apiEquipmentTypesIdPut(String(type.id), {
                category_id: newCategoryId,
                name: type.name,
                name_en: type.name_en || '',
                description: type.description || '',
              })
              .toPromise()
          );
          Promise.all(updatePromises)
            .then(() => this.getAll())
            .catch(() => this.getAll());
        } else {
          this.getAll();
        }
      });
  }

}
